<template>
  <div class="mosaique-overlay" :style="overlayBgStyle">
    <div class="closeButtons">
      <q-btn icon="close" flat round dense @click="$emit('closeMosaique')" aria-label="Fermer la mosaïque" />
      <ReturnHomeButton @click="$emit('closeAllDialogs')" />
    </div>

    <section class="gallery-stage">
      <button
        v-if="items.length > 1"
        class="scroll-button scroll-button--top"
        type="button"
        @click="scrollGallery(-1)"
        aria-label="Remonter"
      >
        <q-icon name="keyboard_arrow_up" />
      </button>

      <div ref="galleryRef" class="media-grid">
        <div v-if="loading" class="state-panel">Chargement...</div>
        <div v-else-if="items.length === 0" class="state-panel">Aucune ressource</div>

        <template v-else>
          <article
            v-for="(item, index) in items"
            :key="item.resource_id ?? item.name ?? index"
            class="media-tile"
            :style="{ '--delay': `${Math.min(200 + index * 55, 720)}ms` }"
            role="button"
            tabindex="0"
            :aria-label="item.title || 'Ouvrir la ressource'"
            @click="openLightbox(index)"
            @keyup.enter="openLightbox(index)"
          >
            <img
              v-if="getResourceType(item) === 'image' && item.url"
              :src="item.url"
              :alt="item.title || ''"
              loading="lazy"
            />
            <video
              v-else-if="getResourceType(item) === 'video' && item.url"
              :src="item.url"
              muted
              playsinline
            />
            <div v-else class="file-tile">
              <q-icon :name="fileIcon(item)" />
              <span>{{ item.url === null ? 'Media indisponible' : (item.title || 'Chargement...') }}</span>
            </div>
            <div v-if="item.title" class="tile-caption">
              <span>{{ item.title }}</span>
            </div>
          </article>
        </template>
      </div>

      <button
        v-if="items.length > 1"
        class="scroll-button scroll-button--bottom"
        type="button"
        @click="scrollGallery(1)"
        aria-label="Descendre"
      >
        <q-icon name="keyboard_arrow_down" />
      </button>
    </section>

    <transition name="fade">
      <div v-if="activeIndex !== null" class="lightbox" @click="closeLightbox">
        <div class="lightbox-content" @click.stop>
          <button class="close-button" type="button" @click="closeLightbox" aria-label="Fermer">
            <q-icon name="close" />
          </button>
          <button
            v-if="items.length > 1"
            class="lightbox-nav lightbox-nav--prev"
            type="button"
            @click="navigateLightbox(-1)"
            aria-label="Précédent"
          >
            <q-icon name="chevron_left" />
          </button>

          <img
            v-if="getResourceType(activeItem) === 'image' && activeItem.url"
            :src="activeItem.url"
            :alt="activeItem.title || ''"
          />
          <video
            v-else-if="getResourceType(activeItem) === 'video' && activeItem.url"
            :src="activeItem.url"
            controls
            autoplay
            playsinline
          />
          <div v-else class="file-tile file-tile--large">
            <q-icon :name="fileIcon(activeItem)" />
            <span>{{ activeItem.url === null ? 'Media indisponible' : (activeItem.title || 'Chargement...') }}</span>
          </div>

          <button
            v-if="items.length > 1"
            class="lightbox-nav lightbox-nav--next"
            type="button"
            @click="navigateLightbox(1)"
            aria-label="Suivant"
          >
            <q-icon name="chevron_right" />
          </button>

          <div v-if="activeItem.title" class="lightbox-caption">
            <strong>{{ activeItem.title }}</strong>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import axios from 'axios';
import ReturnHomeButton from './ReturnHomeButton.vue';
import { useOverlayBg } from '../composables/useOverlayBg';

const props = defineProps(['blockDatas']);
defineEmits(['closeMosaique', 'closeAllDialogs']);

const { overlayBgStyle } = useOverlayBg();

const items = ref([]);
const loading = ref(false);
const galleryRef = ref(null);
const activeIndex = ref(null);

const activeItem = computed(() => items.value[activeIndex.value] || {});

const getResourceType = (item) => {
  const name = item?.name || '';
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (/\.(mp4|avi|mov|wmv|webm|mkv)$/i.test(name)) return 'video';
  if (/\.(mp3|wav|ogg|m4a)$/i.test(name)) return 'audio';
  if (/\.(pdf)$/i.test(name)) return 'pdf';
  if (/\.(doc|docx)$/i.test(name)) return 'doc';
  return 'file';
};

const fileIcon = (item) => {
  const type = getResourceType(item);
  if (type === 'audio') return 'audiotrack';
  if (type === 'pdf' || type === 'doc') return 'description';
  return 'insert_drive_file';
};

const fetchMedia = async () => {
  const resources = props.blockDatas?.associatedResources || [];
  loading.value = true;

  try {
    const withUrls = await Promise.all(resources.map(async (resource, position) => {
      let url = null;
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`);
        url = response.data;
      } catch (error) {
        url = null;
      }
      const parsedIndex = Number(resource.index);
      return {
        ...resource,
        url,
        sortIndex: Number.isFinite(parsedIndex) ? parsedIndex : position,
      };
    }));

    withUrls.sort((a, b) => a.sortIndex - b.sortIndex);
    items.value = withUrls;
  } finally {
    loading.value = false;
  }
};

const scrollGallery = (direction) => {
  if (!galleryRef.value) return;
  const amount = Math.max(galleryRef.value.clientHeight * 0.72, 360);
  galleryRef.value.scrollBy({ top: direction * amount, behavior: 'smooth' });
};

const openLightbox = (index) => {
  activeIndex.value = index;
};

const closeLightbox = () => {
  activeIndex.value = null;
};

const navigateLightbox = (direction) => {
  if (!items.value.length || activeIndex.value === null) return;
  activeIndex.value = (activeIndex.value + direction + items.value.length) % items.value.length;
};

onMounted(fetchMedia);
</script>

<style scoped>
.mosaique-overlay {
  background-color: rgba(217, 217, 217, 1);
  position: fixed;
  inset: 0;
  z-index: 9999;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: overlayFadeIn 200ms ease both;
}

.closeButtons {
  position: absolute;
  top: 16px;
  right: 16px;
  width: auto;
  margin: 0;
  display: flex;
  gap: 10px;
  z-index: 10001;
}

.gallery-stage {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 96px 56px;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 40px;
  width: 100%;
  height: auto;
  max-height: calc(100vh - 192px);
  margin: 0 auto;
  overflow-y: auto;
  scroll-snap-type: y proximity;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding: 0;
}

.media-grid::-webkit-scrollbar {
  display: none;
}

.media-tile {
  position: relative;
  aspect-ratio: 1.22 / 1;
  overflow: hidden;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.55);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  scroll-snap-align: center;
  animation: tileIn 460ms ease both;
  animation-delay: var(--delay);
  transition: transform 180ms ease, filter 180ms ease;
}

.media-tile:hover,
.media-tile:focus-visible {
  transform: translateY(-3px);
  filter: brightness(1.03);
  outline: none;
}

.media-tile:active {
  transform: scale(0.985);
}

.media-tile img,
.media-tile video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  transition: transform 520ms ease;
}

.media-tile:hover img,
.media-tile:hover video {
  transform: scale(1.035);
}

.tile-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 36px 18px 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.65), transparent);
  color: #fff;
  font-size: 15px;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 160ms ease, transform 160ms ease;
}

.media-tile:hover .tile-caption,
.media-tile:focus-visible .tile-caption {
  opacity: 1;
  transform: translateY(0);
}

.scroll-button,
.close-button,
.lightbox-nav {
  border: 0;
  cursor: pointer;
}

.scroll-button {
  position: absolute;
  left: 50%;
  z-index: 5;
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff;
  color: #1f1f1f;
  font-size: 34px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transform: translateX(-50%);
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.scroll-button:hover {
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
}

.scroll-button:active {
  transform: translateX(-50%) scale(0.94);
}

.scroll-button--top {
  top: 18px;
}

.scroll-button--bottom {
  bottom: 18px;
}

.file-tile {
  display: grid;
  place-items: center;
  gap: 14px;
  height: 100%;
  padding: 28px;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
  color: #1f1f1f;
}

.file-tile .q-icon {
  font-size: 56px;
  color: #1f1f1f;
}

.file-tile--large {
  width: min(1180px, 94vw);
  height: min(820px, 88vh);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.file-tile--large .q-icon {
  color: #fff;
}

.state-panel {
  grid-column: 1 / -1;
  display: grid;
  place-items: center;
  min-height: 50vh;
  color: #3a3a3a;
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: grid;
  place-items: center;
  padding: 22px;
  background: rgba(0, 0, 0, 0.9);
}

.lightbox-content {
  position: relative;
  display: grid;
  place-items: center;
  width: min(1180px, 94vw);
  height: min(820px, 88vh);
}

.lightbox-content img,
.lightbox-content video {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.close-button,
.lightbox-nav {
  position: absolute;
  z-index: 2;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #fff;
  color: #000;
}

.close-button {
  top: 10px;
  right: 10px;
  width: 44px;
  height: 44px;
}

.lightbox-nav {
  top: 50%;
  width: 58px;
  height: 58px;
  font-size: 34px;
  transform: translateY(-50%);
}

.lightbox-nav--prev {
  left: 10px;
}

.lightbox-nav--next {
  right: 10px;
}

.lightbox-caption {
  position: absolute;
  left: 18px;
  bottom: 18px;
  max-width: 520px;
  padding: 14px 16px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.64);
  color: #fff;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes overlayFadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes tileIn {
  from {
    opacity: 0;
    transform: translateY(18px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1060px) {
  .gallery-stage {
    padding: 72px 24px 28px;
  }

  .media-grid {
    max-height: 78vh;
    gap: 20px;
  }
}

@media (max-width: 680px) {
  .media-grid {
    grid-template-columns: 1fr;
    max-height: none;
    overflow: visible;
    padding-bottom: 24px;
  }

  .scroll-button {
    display: none;
  }
}
</style>
